// Employee-recorded significant dates — net-new spec surface (docs/12 §11.1 /
// docs/13 list "complex key date prediction/configuration" as deferred "Later
// phase"; this is that phase, built out per PO direction).
//
// Distinct from `keyDateAwareness` (packages/shared/src/preferences.ts), which
// is only a CONSENT signal ("is it OK to be aware of significant dates").
// This module is the actual recorded dates themselves — a loved one's
// birthday, a wedding anniversary, another personally difficult date that
// recurs annually. Month + day only, no year: these are recurring, not
// one-off events (the loss date itself already has its own machinery via
// `bereavement_cases.dateOfLoss` and the `Anniversary` touch point).

import { z } from 'zod';
import { preferenceVisibleTo } from './preference-visibility.ts';

// How many days ahead a recorded date counts as "upcoming" for both the
// Manager-nudge cron sweep and the coarse Manager-mode Maeve signal — matches
// the existing `touchpoint_cadence_notification` 7-day-before offset (a
// cadence this codebase already understands) rather than inventing a new
// number. Confirmed as a starting point, not a hard product requirement.
export const SIGNIFICANT_DATE_REMINDER_WINDOW_DAYS = 7;

export const SIGNIFICANT_DATE_TYPES = [
  'loved_one_birthday',
  'wedding_anniversary',
  'death_anniversary_other',
  'other',
] as const;
export type SignificantDateType = (typeof SIGNIFICANT_DATE_TYPES)[number];

export const SIGNIFICANT_DATE_TYPE_LABELS: Record<SignificantDateType, string> = {
  loved_one_birthday: "A loved one's birthday",
  wedding_anniversary: 'Wedding anniversary',
  death_anniversary_other: 'Anniversary of another loss',
  other: 'Other significant date',
};

// Shared between the employee-portal add/edit form (`SignificantDatesList`)
// and the HR/Manager read panel (`SignificantDatesPanel`) — was independently
// duplicated verbatim in both before, with no signal that a future format
// change (abbreviation, localization) needed to land in both places.
export const SIGNIFICANT_DATE_MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

// Rejects invalid month/day pairs (e.g. 31 Feb). Feb 29 is allowed — the
// roll-forward policy for non-leap years is applied by `computeNextOccurrence`.
function isValidMonthDay(month: number, day: number): boolean {
  const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const max = daysInMonth[month - 1];
  return max !== undefined && day <= max;
}

export const CreateSignificantDateRequestSchema = z
  .object({
    dateType: z.enum(SIGNIFICANT_DATE_TYPES),
    label: z.string().trim().min(1).max(120),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
  })
  .strict()
  .refine((v) => isValidMonthDay(v.month, v.day), {
    message: 'invalid_month_day',
    path: ['day'],
  });
export type CreateSignificantDateRequest = z.infer<typeof CreateSignificantDateRequestSchema>;

// Update is a full replace of the editable fields (PUT semantics) — same
// convention as `UpdateResourceRequestSchema`.
export const UpdateSignificantDateRequestSchema = CreateSignificantDateRequestSchema;
export type UpdateSignificantDateRequest = z.infer<typeof UpdateSignificantDateRequestSchema>;

// The employee's own, full-detail view of a recorded date.
export interface SignificantDate {
  id: string;
  dateType: SignificantDateType;
  label: string;
  month: number;
  day: number;
}

// Pure, unit-testable next-occurrence calculator shared by the repository
// (stamps `nextOccurrenceAt` at create/update) and the daily sweep (rolls a
// passed occurrence forward by a year). If `from`'s month/day is on-or-before
// `(month, day)`, the occurrence is this year; otherwise next year.
//
// Feb 29 in a non-leap target year rolls forward to Mar 1 (rather than back
// to Feb 28) — the target year's `daysInMonth` for February already reflects
// leap-year status via `Date`'s own carry behaviour, so `new Date(year, 1, 29)`
// on a non-leap year naturally lands on Mar 1.
export function computeNextOccurrence(month: number, day: number, from: Date): Date {
  const fromYear = from.getFullYear();
  const candidate = new Date(fromYear, month - 1, day, 0, 0, 0, 0);
  if (candidate.getTime() >= startOfDay(from).getTime()) return candidate;
  return new Date(fromYear + 1, month - 1, day, 0, 0, 0, 0);
}

// Exported so every "is this date within N days" check (the repository's
// window queries, the coarse EXISTS check embedded in the case-list query)
// shares one definition of "today" rather than each hand-rolling its own —
// see the day-of-event boundary bug this fixed.
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

// Same rationale as `startOfDay`: every "window end" computation for this
// feature (the repository's window queries, the case-list EXISTS check, the
// reminder sweep) was independently hand-rolling `now.getTime() + days *
// 24*60*60*1000` — one shared definition so a future change to the arithmetic
// (e.g. DST-aware day boundaries) can't be applied to some call sites and
// missed on others.
export function addDays(d: Date, days: number): Date {
  return new Date(d.getTime() + days * 24 * 60 * 60 * 1000);
}

// §11.2-style visibility matrix reuse — same `preferenceVisibleTo` predicate
// as `keyDateAwareness` ('significantDates' → HR full, Manager limited), but
// the projection shape differs per role since this entity isn't a scalar
// preference: HR gets the full list, Manager gets only a coarse boolean.
//
// Always-present shape (not a `{visible} | {hasUpcoming}` discriminated
// union): a union forces every consumer to narrow via `'visible' in x`
// before reading either field, and a consumer that forgets the check reads
// `undefined` off the wrong branch with no compile error — exactly the
// failure mode an untyped e2e fixture hit once already. `visible: null`
// means "not visible to this role" (Manager); `hasUpcoming` is always
// populated so a Manager-shaped value is never a silent dead end.
export interface SignificantDatesForRole {
  visible: SignificantDate[] | null;
  hasUpcoming: boolean;
}

// Append-only audit trail — mirrors `TouchpointAuditEvent`/`TouchpointAuditEntry`
// (packages/shared/src/cases.ts), the correct 1-to-many audit pattern (as
// opposed to `employee_support_preferences_audit`'s full-snapshot-per-mutation
// shape, which only fits a singleton-per-employee row).
export const SIGNIFICANT_DATE_AUDIT_EVENTS = [
  'created',
  'updated',
  'deleted',
  'reminder_dispatched',
] as const;
export type SignificantDateAuditEvent = (typeof SIGNIFICANT_DATE_AUDIT_EVENTS)[number];

export interface SignificantDateAuditEntry {
  id: string;
  significantDateId: string;
  event: SignificantDateAuditEvent;
  actorUserId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export function significantDatesForRole(
  role: 'HR' | 'Manager',
  dates: SignificantDate[],
  hasUpcoming: boolean,
): SignificantDatesForRole {
  const level = preferenceVisibleTo(role, 'significantDates');
  // `hasUpcoming` is always populated regardless of role — it's already
  // computed by the caller (the coarse case-level signal) and is cheap to
  // include even for HR, who additionally gets the full list. 'limited' and
  // 'none' both collapse to `visible: null` today — there is no role below
  // Manager that reads this projection, so 'none' is currently unreachable,
  // but the fallback keeps this total rather than partial.
  return { visible: level === 'full' ? dates : null, hasUpcoming };
}
